import React, { useState } from "react";
import _ from "lodash";
import { SpinnerLoader } from "ToddleAnalytics/components";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import { IconButton, Tooltip } from "@toddle-edu/ds-web";
import {
  CloseOutlined,
  CopyOutlined,
  TickLargeOutlined,
} from "@toddle-edu/ds-icons";
import { useI18n } from "Hooks";
import classes from "./MeetingLinkDetails.scss";
import classNames from "classnames";
import { copyToClipboard } from "Utils";

const MeetingLinkLoader = ({ t, meetingContainerClassName }) => {
  return (
    <div
      className={classNames(classes.loaderContainer, meetingContainerClassName)}
    >
      <div className={classes.loaderHeader}>
        <SpinnerLoader
          borderTopColor={"var(--interactive-secondary)"}
          borderWidth={3}
        />
        <span className="text-body-2">
          {t("calendar:generating_meeting_link")}...
        </span>
      </div>
      <div className={classes.loaderContent}>
        <SkeletonLoader height="20px" width="100%" variant="rounded" />
      </div>
    </div>
  );
};

/**
 * Component that displays meeting link details including URL, meeting ID, and passcode
 * with copy-to-clipboard functionality.
 *
 * @param {Object} props - Component props
 * @param {string} props.meetingUrl - The meeting URL to display and link to
 * @param {string} [props.meetingId] - Optional meeting ID
 * @param {string} [props.passcode] - Optional meeting passcode
 * @param {Function} [props.onRemoveMeetingLink] - Optional callback function to close the display
 * @param {Function} props.t - Translation function for internationalization
 * @param {string} [props.meetingLinkClassName] - Optional additional class name for meeting link styling
 * @param {string} [props.meetingContainerClassName] - Optional additional class name for container styling
 * @param {boolean} [props.showSeparator=true] - Flag to show separator between link and details
 * @returns {JSX.Element} Meeting link display component
 */
const MeetingLinkDisplay = ({
  meetingUrl,
  meetingId,
  passcode,
  onRemoveMeetingLink,
  t,
  meetingLinkClassName,
  meetingContainerClassName,
  showSeparator = true,
}) => {
  const [copiedField, setCopiedField] = useState(null);

  /**
   * Copies text to clipboard and shows a temporary success indicator
   *
   * @param {string} text - The text to copy to clipboard
   * @param {string} fieldName - The field identifier to track which field was copied
   */
  const handleCopy = (text, fieldName) => {
    if (!_.isEmpty(text)) {
      copyToClipboard(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const hasDetails = !_.isEmpty(meetingId) || !_.isEmpty(passcode);

  return (
    <div
      className={classNames(
        classes.meetingContainer,
        meetingContainerClassName
      )}
    >
      <div className={classes.meetingLinkHeader}>
        <a
          href={meetingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={classNames(
            "text-label",
            classes.meetingLink,
            meetingLinkClassName
          )}
        >
          {meetingUrl}
        </a>
        {onRemoveMeetingLink ? (
          <Tooltip
            dsVersion="2.0"
            tooltip={t("calendar:remove_link")}
            placement={"top"}
            showArrow={true}
          >
            <IconButton
              dsVersion="2.0"
              variant="neutral"
              type="plain"
              size="small"
              icon={<CloseOutlined variant="subtle" />}
              onClick={onRemoveMeetingLink}
            />
          </Tooltip>
        ) : null}
      </div>
      {hasDetails && showSeparator ? (
        <div className={classes.separator} />
      ) : null}
      {!_.isEmpty(meetingId) ? (
        <div className={classes.meetingDetailsRow}>
          <span className={classNames("text-body-s", classes.label)}>
            {t("email:meeting_id")}:
          </span>
          <span className={classNames("text-label-s", classes.value)}>
            {meetingId}
          </span>
          <Tooltip
            dsVersion="2.0"
            tooltip={t("calendar:copy_to_clipboard")}
            placement="right"
            showArrow={true}
          >
            <IconButton
              dsVersion="2.0"
              variant="neutral"
              type="plain"
              size="small"
              icon={
                copiedField === "meetingId" ? (
                  <TickLargeOutlined variant="subtle" />
                ) : (
                  <CopyOutlined variant="subtle" />
                )
              }
              onClick={() => handleCopy(meetingId, "meetingId")}
            />
          </Tooltip>
        </div>
      ) : null}
      {!_.isEmpty(passcode) ? (
        <div className={classes.meetingDetailsRow}>
          <span className={classNames("text-body-s", classes.label)}>
            {t("email:passcode")}:
          </span>
          <span className={classNames("text-label-s", classes.value)}>
            {passcode}
          </span>
          <Tooltip
            dsVersion="2.0"
            tooltip={t("calendar:copy_to_clipboard")}
            placement="right"
            showArrow={true}
          >
            <IconButton
              dsVersion="2.0"
              variant="neutral"
              type="plain"
              size="small"
              icon={
                copiedField === "passcode" ? (
                  <TickLargeOutlined variant="subtle" />
                ) : (
                  <CopyOutlined variant="subtle" />
                )
              }
              onClick={() => handleCopy(passcode, "passcode")}
            />
          </Tooltip>
        </div>
      ) : null}
    </div>
  );
};

/**
 * Component that displays meeting link details for third-party video conferencing platforms
 * (Google Meet, Microsoft Teams, Zoom). Shows a loader while creating the link, a button to
 * create a link if none exists, or displays the meeting link with optional meeting ID and passcode.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isCreatingThirdPartyMeetingLink - Flag indicating if a meeting link is being created
 * @param {Object} props.thirdPartyMeetingLinkData - Meeting link data from third-party provider
 * @param {string} props.thirdPartyMeetingLinkData.type - Type of meeting provider ('GOOGLE', 'MICROSOFT', 'ZOOM')
 * @param {Object} props.thirdPartyMeetingLinkData.meetingData - Meeting details (url, meetingCode, password, etc.)
 * @param {Function} props.onRemoveMeetingLink - Callback function to close the meeting link display
 * @param {boolean} props.isFullScreenMode - Flag indicating if the modal is in full-screen mode
 * @param {Function} props.createThirdPartyMeetingLink - Callback function to create a new meeting link
 * @param {string} [props.testId] - Optional test ID for testing purposes
 * @param {string} props.creatorId - ID of the user creating the meeting link
 * @param {boolean} [props.showSeparator=true] - Flag to show separator between link and details
 * @returns {JSX.Element} Meeting link details component
 */
const MeetingLinkDetails = ({
  isCreatingThirdPartyMeetingLink,
  thirdPartyMeetingLinkData,
  onRemoveMeetingLink,
  createThirdPartyMeetingLink,
  isFullScreenMode,
  testId = "",
  creatorId,
  showSeparator = true,
}) => {
  const { t } = useI18n();

  const meetingLinkClassName = isFullScreenMode
    ? classes.fullScreenMeetingLink
    : "";

  const meetingContainerClassName = isFullScreenMode
    ? classes.fullScreenMeetingContainer
    : "";
  const renderGoogleMeetingLink = () => {
    const { meetingData } = thirdPartyMeetingLinkData || {};
    return (
      <MeetingLinkDisplay
        meetingUrl={meetingData?.url || ""}
        meetingId={meetingData?.meetingCode}
        onRemoveMeetingLink={onRemoveMeetingLink}
        t={t}
        meetingLinkClassName={meetingLinkClassName}
        meetingContainerClassName={meetingContainerClassName}
        showSeparator={showSeparator}
      />
    );
  };

  const renderMicrosoftMeetingLink = () => {
    const { meetingData } = thirdPartyMeetingLinkData || {};
    return (
      <MeetingLinkDisplay
        meetingUrl={meetingData?.joinWebUrl || ""}
        meetingId={meetingData?.meetingCode}
        passcode={meetingData?.passcode}
        onRemoveMeetingLink={onRemoveMeetingLink}
        t={t}
        meetingLinkClassName={meetingLinkClassName}
        meetingContainerClassName={meetingContainerClassName}
        showSeparator={showSeparator}
      />
    );
  };

  const renderZoomMeetingLink = () => {
    const { meetingData, createdBy } = thirdPartyMeetingLinkData || {};
    const joinUrl =
      creatorId === createdBy?.id
        ? meetingData?.startUrl
        : meetingData?.joinUrl;
    return (
      <MeetingLinkDisplay
        meetingUrl={joinUrl || ""}
        meetingId={meetingData?.meetingCode}
        passcode={meetingData?.passcode}
        onRemoveMeetingLink={onRemoveMeetingLink}
        t={t}
        meetingLinkClassName={meetingLinkClassName}
        meetingContainerClassName={meetingContainerClassName}
        showSeparator={showSeparator}
      />
    );
  };

  const getMeetingContentFunctionMapping = {
    GOOGLE: renderGoogleMeetingLink,
    MICROSOFT: renderMicrosoftMeetingLink,
    ZOOM: renderZoomMeetingLink,
  };

  const renderThirdPartyMeetingLinkDataHandler =
    getMeetingContentFunctionMapping[thirdPartyMeetingLinkData?.type];
  return (
    <div className={classes.container}>
      {isCreatingThirdPartyMeetingLink ? (
        <MeetingLinkLoader
          t={t}
          meetingContainerClassName={meetingContainerClassName}
        />
      ) : (
        <div>
          {_.isEmpty(thirdPartyMeetingLinkData) ? (
            <button
              className={classNames("text-label-s", classes.noMeetingLinkAdded)}
              onClick={createThirdPartyMeetingLink}
              data-test-id={`${testId}-MeetingLinkDetails-RegenerateLink`}
            >
              {t("calendar:no_meeting_link_added")}
            </button>
          ) : (
            renderThirdPartyMeetingLinkDataHandler?.()
          )}
        </div>
      )}
    </div>
  );
};

export default MeetingLinkDetails;
